import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import React, { useCallback, useEffect, useRef, useState } from 'react';

export interface CameraCaptureProps {
  onCapture: (blob: Blob) => void;
  onClear?: () => void;
  disabled?: boolean;
  /** 'user' for front camera (selfie), 'environment' for back camera (document) */
  facingMode?: 'user' | 'environment';
  /** Ideal video width */
  width?: number;
  /** Ideal video height */
  height?: number;
  /** Mirror the video preview (e.g. for selfies) */
  mirror?: boolean;
  /** Show oval overlay guide (for selfie framing) */
  showOverlay?: boolean;
  /** objectFit for captured image preview */
  previewObjectFit?: 'cover' | 'contain';
  /** Alt text for captured image */
  altText?: string;
}

const CameraCapture = ({
  onCapture,
  onClear,
  disabled,
  facingMode = 'user',
  width = 640,
  height = 480,
  mirror = false,
  showOverlay = false,
  previewObjectFit = 'cover',
  altText = 'Captured',
}: CameraCaptureProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [streamReady, setStreamReady] = useState(false);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setStreamReady(false);
  }, []);

  useEffect(() => {
    let mounted = true;

    const startCamera = async () => {
      try {
        setError(null);
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode:
              facingMode === 'environment' ? { ideal: 'environment' } : 'user',
            width: { ideal: width },
            height: { ideal: height },
          },
        });
        if (mounted && videoRef.current) {
          streamRef.current = stream;
          videoRef.current.srcObject = stream;
          setStreamReady(true);
        } else {
          stream.getTracks().forEach((track) => track.stop());
        }
      } catch (err) {
        if (mounted) {
          setError(
            'Could not access camera. Please allow camera access and try again.'
          );
        }
      }
    };

    startCamera();
    return () => {
      mounted = false;
      stopStream();
    };
  }, [stopStream, facingMode, width, height]);

  // Re-attach stream to video when switching back from captured image (retake)
  useEffect(() => {
    if (!capturedImage && streamRef.current && videoRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [capturedImage]);

  const handleCapture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !streamReady || video.readyState !== 4) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (blob) {
          setCapturedImage(URL.createObjectURL(blob));
          onCapture(blob);
        }
      },
      'image/jpeg',
      0.92
    );
  };

  const handleRetake = () => {
    if (capturedImage) {
      URL.revokeObjectURL(capturedImage);
      setCapturedImage(null);
      onClear?.();
    }
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      width="100%"
      maxWidth={400}
      marginX="auto"
    >
      {error && (
        <Box mb={2} color="error.main" textAlign="center">
          {error}
        </Box>
      )}
      <Box
        position="relative"
        width="100%"
        style={{
          aspectRatio: `${width}/${height}`,
          maxWidth: 400,
          overflow: 'hidden',
          borderRadius: 8,
          backgroundColor: '#000',
        }}
      >
        {capturedImage ? (
          <img
            src={capturedImage}
            alt={altText}
            style={{
              width: '100%',
              height: '100%',
              objectFit: previewObjectFit,
            }}
          />
        ) : (
          <>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transform: mirror ? 'scaleX(-1)' : undefined,
              }}
            />
            {showOverlay && (
              <Box
                position="absolute"
                top="50%"
                left="50%"
                style={{
                  transform: 'translate(-50%, -50%)',
                  width: '45%',
                  height: '80%',
                  border: '3px solid rgba(255,255,255,0.6)',
                  borderRadius: '50%',
                  pointerEvents: 'none',
                }}
              />
            )}
          </>
        )}
      </Box>
      <canvas ref={canvasRef} style={{ display: 'none' }} />
      <Box mt={2} display="flex">
        {capturedImage ? (
          <Button variant="outlined" onClick={handleRetake} disabled={disabled}>
            Retake
          </Button>
        ) : (
          <Button
            variant="contained"
            color="primary"
            onClick={handleCapture}
            disabled={disabled || !streamReady}
          >
            Capture
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default CameraCapture;
